<template>
  <form class="c-form" @submit.prevent="submit">
    <fieldset class="c-form__fields">
      <div class="c-form__field c-field">
        <!-- name -->
        <div class="c-field__wrapper">
          <label for="release_name" class="c-field__label">{{ t('name') }}</label>
          <input
            id="release_name"
            :value="modelValue.name"
            type="text"
            name="release_name"
            class="c-field__input"
            :disabled="loading"
            required
            @input="inputName"
          >
          <!-- safe to use here as it's a static string -->
          <!-- eslint-disable-next-line vue/no-v-html -->
          <small class="c-field__hint" v-html="t('nameHint')" />
        </div>
      </div>
      <!-- description -->
      <div class="c-form__field c-field">
        <div class="c-field__wrapper">
          <label for="release_description" class="c-field__label">{{ t('description') }}</label>
          <input
            id="release_description"
            :value="modelValue.description"
            type="text"
            name="release_description"
            class="c-field__input"
            :disabled="loading"
            @input="inputDescripion"
          >
          <!-- safe to use here as it's a static string -->
          <!-- eslint-disable-next-line vue/no-v-html -->
          <small class="c-field__hint">{{ t('descriptionHint') }}</small>
        </div>
      </div>
      <!-- release notes -->
      <div class="c-form__field c-field">
        <div class="c-field__wrapper">
          <label for="release_notes" class="c-field__label">{{ t('releaseNotes') }}</label>
          <textarea
            id="release_notes"
            :value="modelValue.releaseNotes"
            type="text"
            name="release_notes"
            class="c-field__input"
            :disabled="loading"
            @input="inputReleaseNotes"
          />
          <!-- safe to use here as it's a static string -->
          <!-- eslint-disable-next-line vue/no-v-html -->
          <small class="c-field__hint">{{ t('releaseNotesHint') }}</small>
        </div>
      </div>
    </fieldset>
    <!-- assets -->
    <fieldset class="c-form__fields">
      <label class="c-field__label">{{ t('assets') }}</label>
      <!-- Assets are not editable once the release is created -->
      <ProjectAssetsField
        :editable="!isEdit"
        :model-value="modelValue.assets"
        @update:is-uploading="isAssetUploading = $event"
        @update:is-error="isAssetError = $event"
        @update:model-value="$emit('update:modelValue', { ...modelValue, assets: $event })"
      />
      <div v-if="isEdit && modelValue.assets.length === 0">
        <small class="c-field__hint">{{ t('noAssetsUploaded') }}</small>
      </div>
    </fieldset>
    <!-- submit -->
    <fieldset class="c-form__actions">
      <div v-if="showAssetUploading" class="c-message c-message--warning u-align-center u-mbs">
        <small>{{ t('assetUploading') }}</small>
      </div>
      <div v-if="showAssetError" class="c-message c-message--warning u-align-center u-mbs">
        <small>{{ t('assetError') }}</small>
      </div>
      <div v-if="error" class="c-message c-message--error u-align-center u-mbs">
        <small>{{ error.apiMessage ?? (isEdit ? t('unknownErrorEdit') : t('unknownErrorCreate')) }}</small>
      </div>
      <BtnSubmit :loading="loading" :label="isEdit ? t('editRelease') : t('createRelease')" />
      <!-- safe to use here as it's a static string -->
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p v-if="!isEdit" class="u-mts c-form__links" v-html="t('createInfo')" />
    </fieldset>
  </form>
</template>

<script setup lang="ts">
import { ref, watchEffect } from 'vue'
import { Asset, APIError } from '@/utils/api'
import { useI18n } from '@/utils/i18n'
import ProjectAssetsField from '@/components/ProjectAssetsField.vue'
import BtnSubmit from '@/components/buttons/BtnSubmit.vue'

export interface ReleaseForm {
  name: string
  description: string
  releaseNotes: string
  assets: Asset[]
}

const props = withDefaults(defineProps<{
  modelValue: ReleaseForm
  loading: boolean
  error?: APIError
  isEdit?: boolean
}>(), { error: undefined, isEdit: false })

const emit = defineEmits<{
  (e: 'update:modelValue', release: ReleaseForm): void
  (e: 'submit'): void
}>()

const { t } = useI18n({
  messages: {
    en: {
      name: 'Name',
      nameHint: '(required) Usually a version number. Have you heard about <a href="https://semver.org/">semver</a>?',

      description: 'Description',
      descriptionHint: '(optional) Usually a platform compatibility indication and/or package format. Displayed below the release name in most case.',

      releaseNotes: 'Release notes',
      releaseNotesHint: '(optional) A text describing changes or additions since the previous version, if any.',

      assets: 'Assets',

      unknownErrorCreate: 'An error occurred while trying to create the release, try again. If this keeps happening, please contact us.',
      unknownErrorEdit: 'An error occurred while trying to edit the release, try again. If this keeps happening, please contact us.',
      createRelease: 'Create the release',
      editRelease: 'Edit the release',
      createInfo: `
        Please consider the release public once created.<br>
        Subscribers will receive an e-mail alert about the new release.
      `,
      assetUploading: 'Please wait until the end of file upload...',
      assetError: 'Please remove files which did not upload',
      noAssetsUploaded: 'No assets were uploaded in this release.'
    },
    fr: {
      name: 'Nom',
      nameHint: '(requis) En général, un numéro de version. Avez-vous entendu parler de <a href="https://semver.org/">semver</a>?',

      description: 'Description',
      descriptionHint: "(optionnel) En général, une indication de compatibilité d'OS et/ou un format de package. Affiché sous le nom de la release.",

      releaseNotes: 'Notes de release',
      releaseNotesHint: '(optionnel) Un texte présentant les changements ou ajouts depuis la version précédente, si applicable.',

      assets: 'Fichiers',

      unknownErrorCreate: 'Une erreur est survenue lors de la création de la release, merci de réessayer. Si le problème se reproduit, merci de nous contacter.',
      unknownErrorEdit: 'An error occurred while trying to edit the release, try again. If this keeps happening, please contact us.',
      createRelease: 'Créer la release',
      editRelease: 'Modifier la release',
      createInfo: `
        Merci de considérer que la release est publique une fois créée.<br>
        Les personnes ayant souscris aux alertes du projet recevront un e-mail
        à propos de la nouvelle release.
      `,
      assetUploading: 'Veuillez attendre la fin du chargement des fichiers...',
      assetError: "Veuillez supprimer les fichiers qui n'ont pas été téléversés",
      noAssetsUploaded: "Aucun fichier n'a été téléversé dans cette release"
    }
  }
})

function inputName (event: Event) {
  const name = (event.currentTarget as HTMLInputElement).value
  emit('update:modelValue', { ...props.modelValue, name })
}

function inputDescripion (event: Event) {
  const description = (event.currentTarget as HTMLInputElement).value
  emit('update:modelValue', { ...props.modelValue, description })
}

function inputReleaseNotes (event: Event) {
  const releaseNotes = (event.currentTarget as HTMLTextAreaElement).value
  emit('update:modelValue', { ...props.modelValue, releaseNotes })
}

// asset validation
const isAssetUploading = ref(false)
const isAssetError = ref(false)
const showAssetUploading = ref(false)
const showAssetError = ref(false)
watchEffect(() => {
  if (!isAssetError.value) {
    showAssetError.value = false
  }
  if (!isAssetUploading.value) {
    showAssetUploading.value = false
  }
})

function validateAssets (): boolean {
  // this is not possible to use Constraint Validation API
  // because file input is hidden and fieldset is always valid
  // see https://developer.mozilla.org/en-US/docs/Web/API/HTMLFieldSetElement#methods
  showAssetUploading.value = false
  showAssetError.value = false

  let valid = true
  if (!isAssetUploading.value) {
    showAssetUploading.value = true
    valid = false
  }
  if (!isAssetError.value) {
    showAssetError.value = true
    valid = false
  }
  return valid
}

function submit () {
  if (!validateAssets()) {
    return
  }
  emit('submit')
}
</script>
